import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../components';
import { ArrowLeft, Check, Crown, Calendar, FileSearch, Target, Megaphone } from 'lucide-react';

export function Consultation() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    eventName: '',
    eventDate: '',
    eventUrl: '',
    challenges: '',
    goals: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to your backend or email service
    console.log('Consultation request:', formData);
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const deliverables = [
    {
      icon: FileSearch,
      title: 'Event Website Audit',
      desc: 'Comprehensive review of your event website UX, copy, and conversion optimization opportunities. Development not included — optimization is.'
    },
    {
      icon: Target,
      title: 'Registration Page Audit',
      desc: 'Analysis of your ticket/registration flow to reduce abandonment and maximize sign-ups. Page development not included.'
    },
    {
      icon: Megaphone,
      title: 'Paid Ads Audit',
      desc: 'Review of your current ad campaigns, targeting, and creative performance. Ad creation not included — strategy & optimization is.'
    },
    {
      icon: Calendar,
      title: 'Event Strategy Session',
      desc: '60-minute 1:1 call to align your marketing tactics with your event goals, timeline, and budget.'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Nav */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        padding: '16px 24px',
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--line)'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Wordmark size={28} />
          </a>
          <a 
            href="/organizers" 
            style={{ 
              fontSize: 14, 
              color: 'var(--ink-2)', 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <ArrowLeft size={16} />
            Back to For Organizers
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '140px 24px 60px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            borderRadius: 999,
            marginBottom: 24
          }}>
            <Crown size={18} style={{ color: '#1a1a1a' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>Premium Service</span>
          </div>

          <h1 style={{ 
            fontSize: 'clamp(32px, 5vw, 48px)', 
            fontWeight: 600, 
            lineHeight: 1.1, 
            marginBottom: 20,
            color: 'var(--ink)'
          }}>
            1:1 Event Marketing<br />
            <span style={{ color: 'var(--primary)' }}>Consultation</span>
          </h1>

          <p style={{ 
            fontSize: 'clamp(17px, 2.5vw, 20px)', 
            color: 'var(--ink-2)', 
            maxWidth: 560, 
            margin: '0 auto 32px',
            lineHeight: 1.6
          }}>
            Bring your event to the front row. Expert audit and strategy session to scale your event marketing.
          </p>

          <div style={{
            display: 'inline-block',
            padding: '16px 32px',
            background: 'var(--primary)',
            color: '#fff',
            borderRadius: 999,
            fontSize: 18,
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
          }}>
            $297 — One-time payment
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{ padding: '60px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 28, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 48,
            color: 'var(--ink)'
          }}>
            What's Included
          </h2>

          <div style={{ display: 'grid', gap: 24 }}>
            {deliverables.map((item, i) => (
              <div 
                key={i}
                style={{
                  display: 'flex',
                  gap: 20,
                  padding: 28,
                  background: 'var(--bg-2)',
                  borderRadius: 16,
                  border: '1px solid var(--line)'
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: 'var(--primary-tint)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <item.icon size={24} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: 20, 
                    fontWeight: 600, 
                    marginBottom: 8,
                    color: 'var(--ink)'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6 }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section style={{ padding: '60px 24px', background: 'var(--bg-2)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 28, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 48,
            color: 'var(--ink)'
          }}>
            How It Works
          </h2>

          <div style={{ display: 'grid', gap: 24 }}>
            {[
              { step: '1', title: 'Book & Pay', desc: 'Complete the form below and submit payment securely.' },
              { step: '2', title: 'Share Your Links', desc: 'Send us your event website, registration page, and any active ad campaigns.' },
              { step: '3', title: 'Receive Audit', desc: 'Get a detailed written audit within 3 business days with actionable recommendations.' },
              { step: '4', title: 'Strategy Call', desc: '60-minute 1:1 session to review findings and plan your next moves.' }
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {item.step}
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, color: 'var(--ink)' }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 15, color: 'var(--ink-2)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ 
            fontSize: 32, 
            fontWeight: 600, 
            textAlign: 'center', 
            marginBottom: 16,
            color: 'var(--ink)'
          }}>
            Ready to scale your event?
          </h2>
          <p style={{ 
            fontSize: 17, 
            color: 'var(--ink-2)', 
            textAlign: 'center',
            marginBottom: 40 
          }}>
            Fill out the form below. We'll review and send payment instructions within 24 hours.
          </p>

          {submitted ? (
            <div style={{
              padding: 48,
              background: 'var(--primary-tint)',
              borderRadius: 16,
              textAlign: 'center'
            }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <Check size={32} style={{ color: '#fff' }} />
              </div>
              <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 12, color: 'var(--ink)' }}>
                Request Received!
              </h3>
              <p style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                We'll review your details and send payment instructions to {formData.email} within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Event Name *
                </label>
                <input
                  type="text"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Event Date *
                </label>
                <input
                  type="date"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Event Website URL
                </label>
                <input
                  type="url"
                  name="eventUrl"
                  value={formData.eventUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Biggest Marketing Challenge *
                </label>
                <textarea
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="What's your biggest struggle with promoting this event?"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--ink)' }}>
                  Your Goals for This Event *
                </label>
                <textarea
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="What does success look like? (attendees, revenue, brand awareness, etc.)"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    background: 'var(--bg)',
                    color: 'var(--ink)',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  marginTop: 12,
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: '#1a1a1a',
                  border: 'none',
                  borderRadius: 999,
                  fontSize: 17,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(255, 215, 0, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Request Consultation — $297
              </button>

              <p style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center' }}>
                Payment instructions will be sent via email after review. 100% satisfaction guarantee.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '40px 24px', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <Wordmark size={20} />
          <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
            <Link to="/about" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>About</Link>
            <Link to="/privacy" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/terms" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Terms</Link>
            <a href="mailto:info@VentyVIP.com" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Contact</a>
          </div>
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>© 2026 VentyVIP</span>
        </div>
      </footer>
    </div>
  );
}
